import { ReactNode, useMemo } from 'react'
import {
  MlCtaBar,
  MlCtaBarProps,
  MlNotification,
  MlNotificationProps,
  MlSiteBanner,
  MlSiteBannerProps,
  MlSnackbar,
  MlSnackbarProps,
} from '../../molecules'
import { FormFieldState, FormProps } from '../or-booking-panel'
import { OrCardCarousel, OrCardCarouselProps } from '../or-card-carousel'
import { sectionBorders } from '../or-flexible-block'
import { OrTextBlock, OrTextBlockProps } from '../or-text-block'
import { OrMapProps } from '../or-map'
import { cn } from '../../utils'
import { OrBaseContent, OrBaseContentProps } from '../or-base-content'
import dynamic from 'next/dynamic'
import { FormRenderer } from './form-renderer'

export interface OrContainerProps {
  containerTitle?: string
  description?: string
  blocks: OrContainerBlock[]
  isFormLoading?: boolean
  onSubmitForm?: (
    valid: boolean,
    errors: string[],
    state: FormFieldState,
    submissionType: string,
    gateMetadata?: { hasGatedContent: boolean; gatedContentFilename: string }
  ) => Promise<void>
  formErrorNotificationProps?: MlNotificationProps | null
  formSuccessSnackbarProps?: MlSnackbarProps | null
  gaCookie?: string
  gaFormField?: string
  errorElementId?: string
  gateUnlocked?: boolean
  onGateUnlock?: (formId: string) => void
  successfulFormId?: string
}

export type OrContainerBlocksType =
  | 'ml-site-banner'
  | 'or-text-block'
  | 'ml-cta-bar'
  | 'form'
  | 'or-card-carousel'
  | 'or-map'
  | 'or-base-content'
  | 'custom-block'

export interface CustomBlock {
  content: ReactNode
}

export interface FormWithTitleAndTypeProps extends FormProps {
  title?: OrTextBlockProps['content']
  formId?: string
  submissionType?: string
  isGate?: boolean
  gateRevealsContentAfter?: boolean
  gateAttachedFileUrl?: string
  sendGatedContentByEmail?: boolean
  /** When set (e.g. from Sanity orForm), overrides template default success snackbar copy */
  successSnackbar?: Omit<MlSnackbarProps, 'dismiss' | 'visible' | 'className'>
  /** When set (e.g. from Sanity orForm), overrides template default server error notification */
  serverErrorNotification?: Omit<MlNotificationProps, 'className'>
  /** Client-side validation: one invalid field (maps from Sanity singleFieldErrorNotification) */
  fieldError?: Omit<MlNotificationProps, 'className'>
  /** Client-side validation: multiple invalid fields (maps from Sanity multipleFieldsErrorNotification) */
  multipleFieldError?: Omit<MlNotificationProps, 'className'>
}

export interface OrContainerBlock {
  type: OrContainerBlocksType
  fields:
    | MlSiteBannerProps
    | OrTextBlockProps['content']
    | MlCtaBarProps
    | FormWithTitleAndTypeProps
    | OrCardCarouselProps
    | OrMapProps
    | OrBaseContentProps
    | CustomBlock
}

const DynamicMapLoader = dynamic(() => import('../../organisms').then((mod) => mod.OrMap), {
  ssr: false,
  loading: () => (
    <div className="h-[28.125rem] sm:h-[32.5rem] slg:h-[22rem] lg:h-[23rem] bg-gray-200 animate-pulse flex items-center justify-center">
      <div className="w-[50px] h-[50px] animate-spin rounded-full border-2 border-current border-t-transparent" />
    </div>
  ),
})

export const OrContainer = ({
  blocks,
  containerTitle,
  description,
  onSubmitForm,
  isFormLoading,
  formErrorNotificationProps,
  formSuccessSnackbarProps,
  gaFormField,
  gaCookie,
  errorElementId,
  gateUnlocked = false,
  onGateUnlock,
  successfulFormId,
}: OrContainerProps) => {
  const gateFormBlockIndex = useMemo(() => {
    return blocks.findIndex(
      (b) =>
        b.type === 'form' &&
        (b.fields as FormWithTitleAndTypeProps).isGate &&
        (b.fields as FormWithTitleAndTypeProps).gateRevealsContentAfter
    )
  }, [blocks])

  const isBlockHiddenByGate = (index: number) => gateFormBlockIndex >= 0 && index > gateFormBlockIndex && !gateUnlocked

  return (
    <article className="lg:mx-auto lg:max-w-screen-lg py-8 first-of-type:pt-0">
      <div className={cn((containerTitle || description) && 'pb-8')}>
        {containerTitle ? (
          <h1 className="px-4 sm:px-8 slg:px-12 lg:px-[10.4375rem] font-heading font-medium leading-1.2 text-40 lg:text-[3.5rem]">
            {containerTitle}
          </h1>
        ) : null}
        {description ? (
          <p className="px-4 lg:px-[10.4375rem] font-heading font-medium text-neutrals-900 pt-2">{description}</p>
        ) : null}
      </div>
      {blocks.map((block, index) => {
        if (isBlockHiddenByGate(index)) return null
        let content: ReactNode = null

        switch (block?.type) {
          case 'ml-site-banner': {
            const fields: MlSiteBannerProps = block?.fields as MlSiteBannerProps

            content = (
              <section className="!pt-0">
                <MlSiteBanner
                  variant={fields?.variant}
                  media={fields?.media}
                  logo={fields?.logo}
                  headline={fields?.headline}
                  description={fields?.description}
                  cta={fields?.cta}
                  orientation={fields?.orientation}
                  alignment={fields?.alignment}
                  locationOptions={fields?.locationOptions}
                  solutions={fields?.solutions}
                  defaultSolutionKey={fields?.defaultSolutionKey}
                />
              </section>
            )
            break
          }

          case 'or-text-block': {
            const fields: OrTextBlockProps['content'] = block?.fields as OrTextBlockProps['content']

            content = <OrTextBlock key={index} content={fields} className="lg:!pb-6" />
            break
          }

          case 'ml-cta-bar': {
            const fields: MlCtaBarProps = block?.fields as MlCtaBarProps

            content = (
              <section className={`${sectionBorders} !pt-0`}>
                <MlCtaBar
                  key={index}
                  className="max-w-[21.438rem] mx-auto"
                  primaryLabel={fields?.primaryLabel}
                  primaryIcon={fields?.primaryIcon}
                  primaryActionValue={fields?.primaryActionValue}
                  primaryTarget={fields?.primaryTarget}
                  secondaryLabel={fields?.secondaryLabel}
                  secondaryIcon={fields?.secondaryIcon}
                  secondaryActionValue={fields?.secondaryActionValue}
                  secondaryTarget={fields?.secondaryTarget}
                  parentName="OrContainer"
                />
              </section>
            )

            break
          }

          case 'form': {
            const fields = block?.fields as FormWithTitleAndTypeProps
            const isGateForm = fields.isGate && fields.formId
            const isGateUnlockedForThisForm = !!(isGateForm && gateUnlocked && successfulFormId === fields.formId)

            const errorNotificationProps: MlNotificationProps | null = formErrorNotificationProps
              ? {
                  ...formErrorNotificationProps,
                  ...(fields.serverErrorNotification ?? {}),
                }
              : null
            const formError = errorNotificationProps ? (
              <MlNotification
                {...errorNotificationProps}
                id={errorElementId || 'or-container-form-error-notification'}
                className="my-10"
              />
            ) : null

            const formSuccess = formSuccessSnackbarProps ? (
              <>
                <div className="my-8" />
                <MlSnackbar
                  {...formSuccessSnackbarProps}
                  {...(fields.successSnackbar ?? {})}
                  id="form_success"
                  className="z-10"
                />
              </>
            ) : null

            content = (
              <section className={sectionBorders} id={fields.formId}>
                {formError}
                {formSuccess}
                <FormRenderer
                  fields={fields.fields ?? []}
                  title={fields.title}
                  onSubmit={
                    onSubmitForm
                      ? (valid, errors, state) =>
                          onSubmitForm(
                            valid,
                            errors,
                            state,
                            fields.submissionType ?? 'General inquiry',
                            fields.isGate && fields.gateAttachedFileUrl && fields.sendGatedContentByEmail
                              ? {
                                  hasGatedContent: true,
                                  gatedContentFilename: fields.gateAttachedFileUrl.split('/').pop() ?? '',
                                }
                              : undefined
                          )
                      : undefined
                  }
                  onGateUnlock={
                    isGateForm && onGateUnlock && fields.formId
                      ? () => onGateUnlock(fields.formId as string)
                      : undefined
                  }
                  isLoading={isFormLoading ?? false}
                  gaFormField={gaFormField ?? ''}
                  gaCookie={gaCookie ?? ''}
                  isGateUnlocked={isGateUnlockedForThisForm}
                  gateAttachedFileUrl={fields.gateAttachedFileUrl}
                  sendGatedContentByEmail={fields.sendGatedContentByEmail}
                  fieldError={fields.fieldError}
                  multipleFieldError={fields.multipleFieldError}
                />
              </section>
            )

            break
          }

          case 'or-card-carousel': {
            const fields = block?.fields as OrCardCarouselProps

            content = <OrCardCarousel {...fields} className="!pb-8" />

            break
          }

          case 'or-map': {
            const fields = block?.fields as OrMapProps
            content = (
              <section className={`${sectionBorders} !pt-0 rounded-8 !pb-8`}>
                <DynamicMapLoader {...fields} />
              </section>
            )

            break
          }

          case 'or-base-content': {
            const fields = block?.fields as OrBaseContentProps

            content = <OrBaseContent {...fields} />

            break
          }

          case 'custom-block': {
            content = (block?.fields as CustomBlock).content
            break
          }
        }

        const nextBlock = blocks[index + 1]
        const nextBlockType = nextBlock?.type
        const addPadding = (() => {
          if (nextBlockType === 'form') {
            return false
          }

          if (nextBlockType === 'custom-block') {
            return false
          }

          if (block.type === 'or-card-carousel') {
            return false
          }

          if (block.type === 'or-map') {
            return false
          }

          if (block.type === 'ml-cta-bar' && nextBlockType !== 'or-text-block') {
            return false
          }

          if (block.type === 'ml-site-banner' && nextBlockType === 'ml-site-banner') {
            return false
          }

          if (block.type === 'ml-site-banner' && nextBlockType === 'or-base-content') {
            return shouldAddPaddingForBaseContent(nextBlock.fields as OrBaseContentProps)
          }

          if (block.type === 'or-base-content' && nextBlockType === 'or-base-content') {
            return shouldAddPaddingForBaseContent(
              nextBlock.fields as OrBaseContentProps,
              block.fields as OrBaseContentProps
            )
          }

          return true
        })()

        return content ? (
          <div className={addPadding ? 'mb-8 last:mb-0' : ''} key={index}>
            {content}
          </div>
        ) : null
      })}
    </article>
  )
}

const shouldAddPaddingForBaseContent = (nextBlock: OrBaseContentProps, currentBlock?: OrBaseContentProps) => {
  if (
    nextBlock.layout === 'contentBlockA' &&
    currentBlock?.layout === nextBlock.layout &&
    currentBlock.fields.background === 'none' &&
    (nextBlock.fields.background === 'gray' || nextBlock.fields.background === undefined)
  ) {
    return true
  }

  const nextBlockHasBuiltinPadding = checkBlockHasBuiltinPadding(nextBlock)
  if (!currentBlock) {
    return !nextBlockHasBuiltinPadding
  }

  const currentBlockHasBuiltinPadding = checkBlockHasBuiltinPadding(currentBlock)
  return !currentBlockHasBuiltinPadding || !nextBlockHasBuiltinPadding
}

const checkBlockHasBuiltinPadding = (block: OrBaseContentProps) => {
  switch (block.layout) {
    case 'contentBlockA': {
      return true
    }

    case 'contentBlockG': {
      return true
    }
  }

  return false
}
